/**
 * @module Model
 */

import { ValidationErrors } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { Document } from '@feature/document-center/shared/document.model';
import { Order } from '@feature/orders/shared';
import {
	ACTRA_NATURE_OF_PRODUCTION, ACTRA_PERFORMANCE_CATEGORY,
	ADD_AGENT_FEE,
	ADD_CAST_COLLABORATION_FEE,
	PLACE_OF_PERFORMANCE,
} from '@app/components/custom-fields/shared/custom-field.model';

/**
 * Class representing an imported order in the application.
 *
 * This class encapsulates various properties related to an imported order, including identifiers,
 * payer and payee information, financial details, and associated metadata. It is used to manage
 * and represent imported order data that is used within the multi-payment process.
 *
 * @class
 * @model
 */
export class OrderImport {
	/**
	 * The unique identifier for the import order. Optional as it can be assigned later.
	 * @type {number | undefined}
	 */
	id?: string;

	/**
	 * Deferred number associated with the import order (optional).
	 * @type {string | number | undefined}
	 */
	deferId?: string | number;

	/**
	 * The title of the import order.
	 * @type {string}
	 */
	title: string;

	/**
	 * The first name of the talent associated with the import order.
	 * @type {string}
	 */
	payeeFirstName: string;

	/**
	 * The middle name of the talent associated with the import order (optional).
	 * @type {string | undefined}
	 */
	payeeMiddleName?: string;

	/**
	 * The last name of the talent associated with the import order.
	 * @type {string}
	 */
	payeeLastName: string;

	/**
	 * The full name of the talent associated with the import order.
	 * @type {string}
	 */
	payeeFullName: string;

	/**
	 * Name of the payee's agency (optional).
	 * @type {string | undefined}
	 */
	agencyName?: string;

	/**
	 * Memo displayed associated with the import order (optional).
	 * @type {string | undefined}
	 */
	memoDisplayed?: string;

	/**
	 * Vendor service performed line item (optional).
	 * @type {string}
	 */
	vendorServicePerformed?: string;

	/**
	 * The number of hours to be converted into digital quantity.
	 * @type {number}
	 */
	hours: number;

	/**
	 * The number of minutes to be converted into digital quantity.
	 * @type {number}
	 */
	minutes: number;

	/**
	 * The rate amount associated with the import order.
	 * @type {number}
	 */
	rate: number;

	/**
	 * Indicates whether the imported order uses a flat rate calculation (optional).
	 * @type {boolean | undefined}
	 */
	isFlatRateCalculation?: boolean;

	/**
	 * Indicates whether the imported order is a lump sum payment (optional).
	 * @type {boolean | undefined}
	 */
	isLumpSumCalculation?: boolean;

	/**
	 * The start date associated with the import order.
	 * @type {string | Date}
	 */
	startDate: Date;

	/**
	 * The finish date associated with the import order.
	 * @type {string | Date}
	 */
	finishDate: Date;

	/**
	 * Array of custom field values associated with the import order (optional).
	 * @type {Array<CustomFieldValue> | undefined}
	 */
	customFieldValues?: Array<string>;

	/**
	 * Creates an instance of the OrderImport model with an empty {@see CustomFieldValue} array.
	 *
	 * @constructor
	 */
	constructor() {
		this.customFieldValues = [];

		// @note ETT-516 Extend the list of custom fields based on the country, if required.
		this.customFieldValues[ADD_AGENT_FEE] = null;
		this.customFieldValues[ADD_CAST_COLLABORATION_FEE] = null;
		this.customFieldValues[PLACE_OF_PERFORMANCE] = null;
		this.customFieldValues[ACTRA_NATURE_OF_PRODUCTION] = null;
		this.customFieldValues[ACTRA_PERFORMANCE_CATEGORY] = null;
	}
}

/**
 * Represents the input data for bulk order import.
 *
 * @class
 * @model
 */
export class OrderBulkImportInput {
	/**
	 * The user identifier associated with the imported order.
	 * @type {number}
	 */
	linkedToUserId: number;

	/**
	 * The payer company identifier associated with the imported order.
	 * @type {number | undefined}
	 */
	payerCompanyId?: number;

	/**
	 * An array of orders to be imported in a bulk.
	 * @type {Array<Order>}
	 */
	orders: Array<Order>;

	/**
	 * Creates an instance of the OrderBulkImportInput.
	 *
	 * @constructor
	 * @param {number} linkedToUserId - The payer id associated with the imported orders.
	 * @param {Array<Order>} orders - An array of orders to be imported in bulk.
	 */
	constructor(linkedToUserId: number, orders: Array<Order>) {
		this.linkedToUserId = linkedToUserId;
		this.orders = orders;
	}
}

/**
 * Represents the output of a bulk order import operation.
 *
 * @class
 * @model
 */
export class OrderBulkImportOutput {
	/**
	 * The order queue identifier created after the import operation.
	 * @type {number}
	 */
	queueId: number;

	/**
	 * Array of affected orders after the import operation.
	 * @type {Array<Order>}
	 */
	affectedOrders: Array<Order>;

	/**
	 * Array of unaffected orders with the details about the import.
	 * @type {Array<UnaffectedOrderImport>}
	 */
	unaffectedOrders: Array<UnaffectedOrderImport>;
}

/**
 * Represents an unaffected imported orders.
 *
 * @class
 * @model
 */
export class UnaffectedOrderImport {
	/**
	 * The unique identifier of the unaffected imported order.
	 * @type {number}
	 */
	orderImportId: number;

	/**
	 * The error message associated with the imported order.
	 * @type {string}
	 */
	errorMsg: string;
}

/**
 * Represents the input data for the order queue and the source document.
 *
 * @class
 * @model
 */
export class OrderQueueDocumentInput {
	/**
	 * The order queue identifier associated with the source document.
	 * @type {number}
	 */
	queueId: number;

	/**
	 * The source document associated with the order queue.
	 * @type {Document}
	 */
	document: Document;

	/**
	 * Creates an instance of the OrderQueueDocumentInput.
	 *
	 * @constructor
	 * @param {number} queueId - The order queue id associated with the document.
	 * @param {Document} document - The document associated with the order queue.
	 */
	constructor(queueId: number, document: Document) {
		this.queueId = queueId;
		this.document = document;
	}
}

/**
 * Represents details of an imported order, including validation errors.
 *
 * @class
 * @model
 */
export class OrderImportDetails {
	/**
	 * The unique identifier associated with the imported order.
	 * @type {number}
	 */
	importId: string;

	/** The order received after mapping of the import details */
	order?: Order;

	/** The order import containing import details */
	orderImport: OrderImport;

	/** BehaviorSubject for holding validation errors related to the specific details of an imported order */
	private readonly _validationErrors$: BehaviorSubject<ValidationErrors>;

	/**
	 * Observe changes to the validationErrors property
	 * @returns {Observable<ValidationErrors>} An observable of validation errors.
	 */
	get validationErrors$(): Observable<ValidationErrors> { return this._validationErrors$.asObservable(); }

	/**
	 * Exposes the validation errors BehaviorSubject
	 * @returns {BehaviorSubject<ValidationErrors>} The validation errors BehaviorSubject.
	 */
	get validationErrors(): ValidationErrors { return this._validationErrors$.getValue(); }

	/**
	 * Sets new values for validation errors and emits changes
	 * @param {Object} errors - The validation errors to set.
	 */
	set validationErrors(errors: ValidationErrors) { this._validationErrors$.next(errors); }

	/**
	 * Constructs the OrderImportDetails object with an empty validation errors BehaviorSubject
	 */
	constructor() {
		this._validationErrors$ = new BehaviorSubject<ValidationErrors>(null);
	}
}
